import i18n from '../i18n'
import store from '../store'
import { clearUser } from '../components/redux/userSlice'

/**
 * Utility function to make authenticated API calls with proper headers
 * @param {string} url - The API endpoint URL
 * @param {Object} options - Fetch options (method, body, etc.)
 * @param {string} token - Auth token
 * @param {string} profileId - Profile ID for X-PROFILE-ID header
 * @returns {Promise<Response>} - Fetch response
 */
export const apiCall = async (url, options = {}, token = null, profileId = null) => {
  const headers = {
    'Content-Type': 'application/json',
    'Accept-Language': i18n.language || 'en',
    ...options.headers,
  }

  // Add auth headers if provided
  if (token) {
    headers.Authorization = `Bearer ${token}`
  }

  if (profileId) {
    headers['X-PROFILE-ID'] = profileId
  }

  const fetchOptions = {
    ...options,
    headers,
  }

  const response = await fetch(url, fetchOptions)

  // Handle 401 Unauthorized responses (token expired/invalid)
  if (response.status === 401) {
    console.log('Received 401 Unauthorized response, clearing user state')
    store.dispatch(clearUser())
  }

  return response
}

/**
 * Utility function to make authenticated API calls to the main API host
 * @param {string} endpoint - The API endpoint (e.g., '/v2/exercises/123')
 * @param {Object} options - Fetch options (method, body, etc.)
 * @param {string} token - Auth token
 * @param {string} profileId - Profile ID for X-PROFILE-ID header
 * @returns {Promise<Response>} - Fetch response
 */
export const apiCallWithHost = async (endpoint, options = {}, token = null, profileId = null) => {
  const url = `${process.env.REACT_APP_API_HOST}${endpoint}`
  return apiCall(url, options, token, profileId)
}

/**
 * Utility function to make authenticated API calls to external services (like R2 worker)
 * @param {string} url - The full URL
 * @param {Object} options - Fetch options (method, body, etc.)
 * @param {string} token - Auth token
 * @param {string} profileId - Profile ID for X-PROFILE-ID header
 * @returns {Promise<Response>} - Fetch response
 */
export const apiCallExternal = async (url, options = {}, token = null, profileId = null) => {
  return apiCall(url, options, token, profileId)
}
